import React from 'react';

import { GestureEvent } from '../../../../../../Events/withEvent';
import { CoordPoint } from '../../../../Utils/Types';
import { BandType } from '../../Laize';
import { LaizeBandItemV3 } from '../../v3/LaizeBandTrace';
import { useLaizeCalcDialog } from '../LaizeCalcDialogProvider';

export const BandsColorPalette = {
    //BAND_NEW_NORMAL: 'rgba(237, 49, 83, 0.3)', not used
    BAND_NEW_HIGHLIGHT: 'rgba(237, 49, 83, 1)',

    COVERED_SURFACE_NORMAL: 'rgba(40, 185, 72, 0.3)',
    COVERED_SURFACE_HIGHLIGHT: '#28B948',

    BAND_REUSED_NORMAL: 'rgba(90, 210, 244, 0.3)',
    BAND_REUSED_HIGHLIGHT: '#5AD2F4',
};

export const MapUILaizeTraces = () => {
    const bands = useLaizeCalcDialog().laizeResult?.bands_v3 || [];

    const bandReusedList = bands.filter((b) => b.type === BandType.Reused);

    const bandReusedPointsList = bandReusedList.map((b) => toPolygonPoints(b.points));
    const bandSurfaceCoveredList = bands.filter(
        (b) => b.type === BandType.SurfaceCovered && !bandReusedPointsList.includes(toPolygonPoints(b.points))
    );

    return (
        <g id="mapLaizeTraces">
            <g id="surfaceCoveredBands">
                {bandSurfaceCoveredList.map((item, index) => (
                    <LaizeBandSurface key={`bandSurfaceCovered-${index}`} item={item} />
                ))}
            </g>
            <g id="reusedBands">
                {bandReusedList.map((item, index) => (
                    <LaizeBandReused key={`bandReused-${index}`} item={item} />
                ))}
            </g>
        </g>
    );
};

export const toPolygonPoints = (points: CoordPoint[] = []) => points.map((p) => `${p.x},${p.y}`).join(' ');

type LaizeBandProps = { item: LaizeBandItemV3 };

const LaizeBandSurface = ({ item }: LaizeBandProps) => {
    const { isBandFocused, hasBandFocused, gestures: GESTURE } = useLaizeBandGesture(item);
    const polygonProps = toBandProps({
        ...GESTURE,
        points: item.points,
        fill: isBandFocused ? BandsColorPalette.COVERED_SURFACE_HIGHLIGHT : BandsColorPalette.COVERED_SURFACE_NORMAL,
    });
    const polygonWithMarginsProps = toBandMarginsProps({ points: item.pointsWithMargins || [] });
    return (
        <g>
            <polygon {...polygonProps} />
            {(isBandFocused || !hasBandFocused) && <polygon {...polygonWithMarginsProps} />}
        </g>
    );
};
const LaizeBandReused = ({ item }: LaizeBandProps) => {
    const { isBandFocused, hasBandFocused, gestures: GESTURE } = useLaizeBandGesture(item);
    const polygonProps = toBandProps({
        ...GESTURE,
        points: item.points,
        fill: isBandFocused ? BandsColorPalette.BAND_REUSED_HIGHLIGHT : BandsColorPalette.BAND_REUSED_NORMAL,
    });
    const polygonWithMarginsProps = toBandMarginsProps({ points: item.pointsWithMargins || [] });
    return (
        <g>
            <polygon {...polygonProps} />
            {(isBandFocused || !hasBandFocused) && <polygon {...polygonWithMarginsProps} />}
        </g>
    );
};

type LaizeBandTraceProps = { points: CoordPoint[]; fill?: string; strokeColor?: string; dashArray?: boolean };
const toBandProps = ({ points, ...rest }: LaizeBandTraceProps): React.SVGAttributes<SVGPolygonElement> => {
    return { points: toPolygonPoints(points), cursor: 'pointer', stroke: '#13123A', strokeWidth: 1, ...rest };
};

const toBandMarginsProps = ({ points }: LaizeBandTraceProps): React.SVGAttributes<SVGPolygonElement> => {
    return {
        points: toPolygonPoints(points),
        fill: 'none',
        stroke: '#13123A',
        strokeWidth: 1,
        strokeDasharray: '10,10',
    };
};

const useLaizeBandGesture = (band: LaizeBandItemV3) => {
    const { focusedBands = {}, setFocusedBands, clearFocusedBands, setZoomedBands, laizeResult } = useLaizeCalcDialog();
    const { bandsFocused = [], focusedFrom } = focusedBands;

    const hasBandFocused = Boolean(bandsFocused.length);
    const isBandFocused = Boolean(bandsFocused.find((b) => b.id === band.id));

    const onMouseEnter = (e: GestureEvent) => {
        setFocusedBands({
            bandsFocused: getBandToFocus(band, laizeResult?.bands_v3),
            focusedFrom: 'FromLaizeBandMap',
        });
    };

    const onClick = (e: GestureEvent) => {
        e.stopPropagation();
        setZoomedBands({ roomId: band.roomId! });
    };

    return {
        isBandFocused,
        focusedFrom,
        hasBandFocused,
        gestures: { onMouseEnter, onMouseLeave: clearFocusedBands, onClick },
    };
};

const getBandToFocus = (band: LaizeBandItemV3, bands: Array<LaizeBandItemV3> = []) => {
    if (band.type === BandType.SurfaceCovered) {
        const realband = bands.find((b) => b.type === BandType.New && b.bandId === band.bandId);
        return realband ? [band, realband] : [band];
    }
    return [band];
};
